import { type } from 'arktype';

export const personTeamQuerySchema = type({
  'personId?': type('string').configure({
    description: 'Filter assignments by person ID. Optional.',
    examples: ['per_abc123'],
  }),
  'teamId?': type('string').configure({
    description: 'Filter assignments by team ID. Optional.',
    examples: ['tea_xyz789'],
  }),
  'role?': type('string').configure({
    description: 'Filter assignments by role within the team. Optional.',
    examples: ['lead'],
  }),
  'startDate?': type('string | null | undefined').configure({
    description: 'Filter assignments whose membership starts on this ISO date (YYYY-MM-DD). Optional and nullable.',
    examples: ['2026-04-22'],
  }),
  'endDate?': type('string | null | undefined').configure({
    description: 'Filter assignments whose membership ends on this ISO date (YYYY-MM-DD). Optional and nullable.',
    examples: ['2026-12-31'],
  }),
}).configure({ description: 'Query parameters for filtering person-team assignments.' });

export type PersonTeamQuery = typeof personTeamQuerySchema.t;

export const createPersonTeamSchema = type({
  personId: type('string').configure({
    description: 'ID of the person being assigned to the team.',
    examples: ['per_abc123'],
  }),
  teamId: type('string').configure({
    description: 'ID of the team the person is joining.',
    examples: ['tea_xyz789'],
  }),
  role: type('string').configure({
    description: 'Role the person holds within the team.',
    examples: ['member', 'lead'],
  }),
  'startDate?': type('string | null | undefined').configure({
    description: 'ISO date (YYYY-MM-DD) when the membership starts. Optional and nullable (open-ended start).',
    examples: ['2026-04-22'],
  }),
  'endDate?': type('string | null | undefined').configure({
    description: 'ISO date (YYYY-MM-DD) when the membership ends. Optional and nullable (open-ended end).',
    examples: ['2026-12-31'],
  }),
}).configure({ description: 'Payload to assign a person to a team with an optional validity period.' });

export type CreatePersonTeam = typeof createPersonTeamSchema.t;

export const updatePersonTeamSchema = type({
  'personId?': type('string').configure({
    description: 'Updated person ID. Optional — rarely changed after creation.',
    examples: ['per_abc123'],
  }),
  'teamId?': type('string').configure({
    description: 'Updated team ID. Optional — rarely changed after creation.',
    examples: ['tea_xyz789'],
  }),
  'role?': type('string').configure({
    description: 'Updated role within the team. Optional.',
    examples: ['lead'],
  }),
  'startDate?': type('string | null | undefined').configure({
    description: 'Updated membership start date (ISO YYYY-MM-DD). Optional and nullable.',
    examples: ['2026-04-22'],
  }),
  'endDate?': type('string | null | undefined').configure({
    description: 'Updated membership end date (ISO YYYY-MM-DD). Optional and nullable.',
    examples: ['2026-12-31'],
  }),
}).configure({ description: 'Partial update payload for an existing person-team assignment.' });

export type UpdatePersonTeam = typeof updatePersonTeamSchema.t;
